import React, { useContext, useEffect, useRef } from "react";
import {
  Button,
  Grid,
  InputLabel,
  Paper,
  TextField,
  IconButton,
  FormControlLabel,
  Checkbox,
  Box,
  Autocomplete,
} from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { CRUDContext } from "./VehiclesTable";
import { endpoints } from "../../Utils/UrlEnum";
import { Controller, useForm, useWatch, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { postData } from "../../Services/postData";
import { VehicleFormProps } from "../../Utils/Types";
import { FormsIds } from "../../Utils/Constants";
import Config from "../../Utils/Config";
import { VehicleFormValidationSchema } from "../../Utils/Models";
import { onFormError } from "../../Utils/Utils";
import { CompactPicker } from "react-color";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Delete } from "@mui/icons-material";
import { ro } from "date-fns/locale";
import { updateVehicle } from "./VehicleUtils";
import useCustomMemo from "../../Hooks/useCustomMemo";
import withRole from "../../Hooks/withRole";

function VehicleForm(props: VehicleFormProps) {
  // Use the provided vehicle or fallback to the CRUDContext
  const vehicleContext: any = useContext(
    props.selectedVehicle ? props.selectedVehicle : CRUDContext
  );
  const formRef = useRef<HTMLFormElement>(null);
  const firstUpdate = useRef(true);
  const memo = useCustomMemo();
  const workPoints = memo?.storage.workPoints || [];
  const restrictByWorkPoint = props.restrictByWorkPoint || false;

  // Initialize the form with Yup validation and default values
  const {
    register,
    control,
    handleSubmit,
    watch,
    getValues,
    formState,
    reset,
  } = useForm({
    resolver: yupResolver(VehicleFormValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      ...(restrictByWorkPoint
        ? { workPointId: localStorage.getItem("userWorkPointId") }
        : {}),
      document_ids: Config.vehicleDocuments.map((doc: string) => ({
        name: doc,
        expirationDate: new Date(),
      })),
    },
    shouldFocusError: true,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });

  // Use useFieldArray for the dynamic document rows
  const { fields, append, remove } = useFieldArray({
    control,
    name: "document_ids",
  });

  const {
    fields: revisionFields,
    append: revisionApend,
    remove: removeRevision,
  } = useFieldArray({
    control,
    name: "revisionIds", // Second field array
  });

  const formData = useWatch({ control });

  // Update context when form data changes
  useEffect(() => {
    if (formData && !firstUpdate.current) {
      props.setContextValue({ ...formData });
    }
  }, [formData]);

  // Populate the form with vehicleContext values on mount if available
  useEffect(() => {
    if (vehicleContext.name && vehicleContext.name !== "") {
      reset(vehicleContext);
    }
  }, [vehicleContext, reset]);

  // Automatically submit the form if needed (based on your original logic)
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    formRef.current?.submit();
  }, []);

  useEffect(() => {
    if (vehicleContext && vehicleContext._id) {
      reset({
        ...vehicleContext,
        document_ids: vehicleContext.document_ids.map((doc: any) => ({
          _id: doc._id || null,
          name: doc.document || "", // If `name` exists, use it; otherwise, default to an empty string
          expirationDate: doc.expirationDate || new Date(),
          document: typeof doc.document === "string" ? doc.document : "", // Keep the path as a string if it exists
        })),
      });
    }
  }, [vehicleContext, reset]);

  // Update an existing element
  const updateElement = async (model: any) => {
    updateVehicle(model, props);
  };

  // Create a new element

  const createElement = async (model: any) => {
    const url = `${endpoints.vehicle.main}`;
    const formData = new FormData();

    // Create an array for metadata, including file name and extension
    const documentsMeta = model.document_ids.map((doc: any) => {
      let documentName = "";
      let documentExtension = "";
      if (doc.document instanceof File) {
        documentName = doc.document.name;
        const parts = doc.document.name.split(".");
        documentExtension = parts.length > 1 ? parts.pop() || "" : "";
      }
      return {
        name: doc.name,
        expirationDate: doc.expirationDate,
        documentName,
        documentExtension,
      };
    });

    // Append metadata as a JSON string.
    formData.append("document_ids_meta", JSON.stringify(documentsMeta));
    formData.append("revisionIds", JSON.stringify(model.revisionIds));

    // Append each file using the key "document_ids".
    model.document_ids.forEach((doc: any) => {
      if (doc.document instanceof File) {
        formData.append("document_ids", doc.document);
      }
    });

    // Append the rest of your model fields.
    Object.keys(model).forEach((key) => {
      if (key !== "document_ids" && key !== "revisionIds") {
        formData.append(key, model[key]);
      }
    });

    try {
      const res = (await postData(url, formData)) as any;
      if (!res || res?.error) {
        console.error("Error response:", res);
        return;
      }
      props.onClose();
      props.setContextValue([]);
    } catch (error) {
      console.error("Error creating vehicle:", error);
    }
  };

  // Handle form submission
  const onSubmit = (model: any) => {
    vehicleContext._id ? updateElement(model) : createElement(model);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onFormError)}
      method="get"
      ref={formRef}
      id={FormsIds.vehicleForm}
    >
      <Grid container spacing={Config.standardGridSpacing}>
        {/* Basic vehicle fields */}
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("brand")}
            id="brand"
            name="brand"
            label={Vocabulary.brand}
            value={getValues("brand") || ""}
            fullWidth
            variant="outlined"
            error={!!formState.errors.brand}
            helperText={formState.errors.brand?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("model")}
            value={getValues("model") || ""}
            name="model"
            id="model"
            label={Vocabulary.model}
            fullWidth
            variant="outlined"
            error={!!formState.errors.model}
            helperText={formState.errors.model?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("chassis")}
            id="chassis"
            name="chassis"
            label={Vocabulary.chassis}
            value={getValues("chassis") || ""}
            fullWidth
            variant="outlined"
            error={!!formState.errors.chassis}
            helperText={formState.errors.chassis?.message?.toString()}
            inputProps={{
              style: { textTransform: "uppercase" }, // This makes the text appear uppercase in the input
            }}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("vehicleNumber")}
            value={getValues("vehicleNumber") || ""}
            name="vehicleNumber"
            id="vehicleNumber"
            label={Vocabulary.vehicleNumber}
            fullWidth
            variant="outlined"
            error={!!formState.errors.vehicleNumber}
            helperText={formState.errors.vehicleNumber?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("modelDate")}
            value={getValues("modelDate") || ""}
            name="modelDate"
            id="modelDate"
            label={Vocabulary.modelDate}
            fullWidth
            variant="outlined"
            error={!!formState.errors.modelDate}
            helperText={formState.errors.modelDate?.message?.toString()}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller
            name="workPointId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                disableClearable
                disablePortal
                disabled={restrictByWorkPoint}
                options={workPoints} // List of work points
                getOptionLabel={(option) => option?.name || ""} // Show name in dropdown
                isOptionEqualToValue={(option, value) =>
                  option?._id === (value?._id || value)
                } // Works for both object and string
                value={
                  workPoints.find(
                    (wp: any) =>
                      wp._id === ((field.value as any)?._id || field.value)
                  ) || null
                } // Ensure correct value selection
                onChange={(_, selectedOption) =>
                  field.onChange(selectedOption?._id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!error}
                    helperText={error?.message}
                    label={Vocabulary.workPoint}
                    type="search"
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={12} lg={6}>
          <InputLabel style={{ margin: "10px 0px" }}>
            {Vocabulary.chooseColor}
          </InputLabel>
          <div style={{ display: "flex" }}>
            <Controller
              name="color"
              control={control}
              render={({ field }) => (
                <CompactPicker
                  color={watch("color")}
                  onChangeComplete={(color: any) => field.onChange(color.hex)}
                />
              )}
            />
            <Paper
              style={{
                width: 50,
                height: 50,
                margin: "0px 30px",
                backgroundColor: watch("color"),
              }}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <Controller
            name="automata"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value || false} />}
                label={Vocabulary.automata}
              />
            )}
          />
        </Grid>

        {/* Document rows */}
        <Grid item xs={12} sm={12} md={12}>
          <Button
            variant="outlined"
            onClick={() =>
              append({
                name: "",
                expirationDate: new Date(),
                document: "",
              })
            }
            style={{ marginTop: "10px", marginBottom: "30px" }}
          >
            {Vocabulary.addDocument}
          </Button>
          <Button
            variant="outlined"
            onClick={() =>
              revisionApend({
                name: "",
                expirationDate: new Date(),
              })
            }
            style={{
              marginLeft: "10px",
              marginTop: "10px",
              marginBottom: "30px",
            }}
          >
            {Vocabulary.addRevizie}
          </Button>
          {fields.map((item, index) => (
            <Grid
              container
              spacing={2}
              alignItems="center"
              key={item.id}
              style={{
                marginBottom: 20,
                backgroundColor: "#f5f5f5",
                paddingBottom: 10,
                borderRadius: 10,
              }}
            >
              <Grid item xs={12} sm={12} md={6}>
                <TextField
                  label={Vocabulary.documentName}
                  {...register(`document_ids.${index}.name` as const)}
                  fullWidth
                  style={{ display: "block", marginBottom: 10 }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Box display="flex" alignItems="center" gap={2}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Controller
                      control={control}
                      name={`document_ids.${index}.expirationDate` as const}
                      render={({ field }) => (
                        <DatePicker
                          label={Vocabulary.documentDate}
                          format="dd/MM/yyyy"
                          value={field.value ? new Date(field.value) : null} // Convert string to Date
                          onChange={(newDate) => field.onChange(newDate)}
                          slots={{ textField: TextField }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              style: { display: "block", marginBottom: 8 },
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                  <IconButton onClick={() => remove(index)}>
                    <Delete />
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          ))}

          {revisionFields.map((item, index) => (
            <Grid
              container
              spacing={2}
              alignItems="stretch"
              key={item.id}
              style={{
                marginBottom: 20,
                backgroundColor: "#f5f5f5",
                paddingBottom: 10,
                borderRadius: 10,
              }}
            >
              {/* Left Column: Stack First 3 Fields */}
              <Grid item xs={12} sm={12} md={6}>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={2}
                  height="100%"
                >
                  <TextField
                    label={Vocabulary.documentName}
                    {...register(`revisionIds.${index}.name` as const)}
                    fullWidth
                  />
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={ro}
                  >
                    <Controller
                      control={control}
                      name={`revisionIds.${index}.expirationDate` as const}
                      render={({ field }) => (
                        <DatePicker
                          label={Vocabulary.documentDate}
                          format="dd/MM/yyyy"
                          value={field.value ? new Date(field.value) : null} // Convert string to Date
                          onChange={(newDate) => field.onChange(newDate)}
                          slots={{ textField: TextField }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                  <TextField
                    label={Vocabulary.price}
                    type="number"
                    {...register(`revisionIds.${index}.price` as const)}
                    fullWidth
                  />
                </Box>
              </Grid>

              {/* Right Column: Full Height Single Field with Delete Button */}
              <Grid item xs={12} sm={12} md={6} alignSelf="stretch">
                <Box display="flex" alignItems="center" gap={2}>
                  <TextField
                    label={Vocabulary.additionalNotes}
                    {...register(`revisionIds.${index}.notes` as const)}
                    fullWidth
                    multiline
                    rows={8} // Adjust height for multiline text
                  />
                  <Box display="flex" justifyContent="flex-end" mt={2}>
                    <IconButton onClick={() => removeRevision(index)}>
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </form>
  );
}

export default withRole(VehicleForm);
